<template>
  <div>
    <component
      :is="usePortal ? 'portal' : 'div'"
      to="top"
      v-if="usePortal ? show : true"
    >
      <div class="filter-wrapper" v-if="!filtersLoaded">
        <FiltersSkeleton />
      </div>
      <div class="filter-wrapper" v-else>
        <div>
          <div class="d-md-none d-flex justify-content-between mb-3 budget">
            <div @click="show = false">
              <b-icon-x-lg></b-icon-x-lg>
            </div>
            <div>
              <h4>{{ $t("listingFilter.filters") }}</h4>
            </div>
            <div
              @click="clearFilters"
              :class="hasFilters ? 'text-main font-weight-bold' : 'text-muted'"
            >
              {{ $t("listingFilter.clear") }}
            </div>
          </div>
          <div class="budget">
            <template v-if="currentConfigKey('showFacilitiesFilter')">
              <h4 v-if="availableFacilities.length > 0">{{ $t("listingFilter.facilities") }}</h4>
              <b-row
                v-for="facility in displayedFacilities"
                :key="facility.name"
                class="mb-1"
              >
                <b-col cols="9" class="checkbox pl-md-3 pl-0">
                  <b-form-checkbox
                    v-model="selectedFacilities"
                    :value="facility.id"
                    size="lg"
                    @change="applyFilters"
                    >{{ getFacilityName(facility.name) }}</b-form-checkbox
                  >
                </b-col>
                <b-col md="2" cols="3" class="text-right checktext">
                  {{ getFacilityCount(facility.id) | numberFormat }}
                </b-col>
              </b-row>
              <span
                v-if="availableFacilities.length > limit"
                class="p-0 small text-main font-weight-bold cursor-pointer"
                @click="showAllFacilities = !showAllFacilities"
              >
                {{
                  showAllFacilities
                    ? $t("listing.showLess")
                    : $tc("listing.showAll", availableFacilities.length)
                }}
              </span>
            </template>
            <template v-if="currentConfigKey('showRatingsFilter')">
              <h4 class="mt-3">{{ $t("listingFilter.starRating") }}</h4>
              <b-row class="mb-1">
                <b-col cols="9" class="checkbox pl-md-3 pl-0">
                  <b-form-checkbox
                    v-model="selectedRatings"
                    value="noRating"
                    size="lg"
                    @change="applyFilters"
                    >{{ $t("listing.noRating") }}</b-form-checkbox
                  >
                </b-col>
                <b-col md="2" cols="3" class="text-right checktext">
                  {{ getFacilityCount("noRating") | numberFormat }}
                </b-col>
              </b-row>
              <b-row v-for="i in 5" :key="i" class="mb-1">
                <b-col cols="9" class="checkbox pl-md-3 pl-0">
                  <b-form-checkbox
                    v-model="selectedRatings"
                    :value="i"
                    size="lg"
                    @change="applyFilters"
                    >{{
                      $tc("listing.ratingStar", i, { count: i })
                    }}</b-form-checkbox
                  >
                </b-col>
                <b-col md="2" cols="3" class="text-right checktext">
                  {{ getFacilityCount(`${i}star`) | numberFormat }}
                </b-col>
              </b-row>
            </template>
            <template v-if="currentConfigKey('showBookingTypeFilter')">
              <h4 class="mt-3">{{ $t("listing.bookingTypes") }}</h4>
              <b-row
                v-for="type in ['A', 'B', 'C', 'D']"
                :key="type"
                class="mb-1"
              >
                <template v-if="showDistributor(type)">
                  <b-col cols="9" class="checkbox pl-md-3 pl-0">
                    <b-form-checkbox
                      v-model="selectedDistributors"
                      :value="type"
                      size="lg"
                      @change="applyFilters"
                      >
                      <span v-html="$tc(`listing.distributorFilter${type}`)"></span></b-form-checkbox
                    >
                  </b-col>
                  <b-col md="2" cols="3" class="text-right checktext">
                    {{ getFacilityCount(type) | numberFormat }}
                  </b-col>
                </template>
              </b-row>
            </template>
            <template v-if="displayedPropertyTypes.length > 0 && currentConfigKey('showPropertyTypeFilter')">
              <h4 class="mt-3">{{ $t("listingFilter.propertyTypes") }}</h4>
              <b-row
                v-for="type in displayedPropertyTypes"
                :key="type.name"
                class="mb-1"
              >
                <b-col cols="9" class="checkbox pl-md-3 pl-0">
                  <b-form-checkbox
                    v-model="selectedTypes"
                    :value="type.id"
                    size="lg"
                    @change="applyFilters"
                    >{{ getPropertyTypeName(type.name) }}</b-form-checkbox
                  >
                </b-col>
                <b-col md="2" cols="3" class="text-right checktext">
                  {{ getFacilityCount(`propertyType_${type.id}`) | numberFormat }}
                </b-col>
              </b-row>
              <span
                v-if="availablePropertyTypes.length > limit"
                class="p-0 small text-main font-weight-bold cursor-pointer"
                @click="showAllPropertyTypes = !showAllPropertyTypes"
              >
                {{
                  showAllPropertyTypes
                    ? $t("listing.showLess")
                    : $tc("listing.showAll", availablePropertyTypes.length)
                }}
              </span>
            </template>
          </div>
          <div class="summary d-md-none d-block">
            <button
              class="btn btn-block g-primary-button"
              :disabled="fetching"
              @click="show = false"
            >
              <b-spinner
                v-if="fetching"
                class="button-spinner"
                label="Spinning"
              ></b-spinner>
              <span v-else-if="results > 0">{{
                $tc("listingFilter.showResults", results, { results })
              }}</span>
              <span v-else>{{ $t("listingFilter.noResult") }}</span>
            </button>
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";
import { currentConfigKey } from "../../helpers/theme";
import FiltersSkeleton from "../Skeletons/FiltersSkeleton.vue";
import TranslationHelper from "../../helpers/translation";
export default {
  name: "Filters",
  props: {
    fetching: {
      type: Boolean,
      required: true,
    },
    results: {
      type: Number,
      required: true,
    },
  },
  components: {
    FiltersSkeleton,
  },
  data() {
    return {
      show: false,
      limit: 5,
      usePortal: false,
      facilities: [],
      countPayload: {},
      propertyTypes: [],
      selectedTypes: [],
      selectedRatings: [],
      filtersLoaded: false,
      selectedFacilities: [],
      facilitiesCountData: {},
      selectedDistributors: [],
      showAllFacilities: false,
      showAllPropertyTypes: false,
    };
  },
  async mounted() {
    this.usePortal = window.innerWidth <= 767;
    window.addEventListener("resize", this.handleResize);
    this.facilities = await this.getAllFacilities();
    this.propertyTypes = await this.getPropertyTypes();
    this.filtersLoaded = true;
  },
  computed: {
    hasFilters() {
      return this.filtersApplied() > 0;
    },
    availableFacilities() {
      const supported = currentConfigKey("supportedFacilities", []);
      return this.facilities.filter(({ id }) => {
        const isSupported = supported.length === 0 || supported.includes(id);
        const count = this.getFacilityCount(id);
        if (count == "") return isSupported;
        return isSupported && Number(count) !== 0;
      });
    },
    availablePropertyTypes() {
      return this.propertyTypes.filter(({ id }) => {
        const count = this.getFacilityCount(`propertyType_${id}`);
        if (count == "") return true;
        return Number(count) !== 0;
      });
    },
    displayedFacilities() {
      if (this.showAllFacilities) {
        return this.availableFacilities;
      }
      return this.availableFacilities.slice(0, this.limit);
    },
    displayedPropertyTypes() {
      if (this.showAllPropertyTypes) {
        return this.availablePropertyTypes;
      }
      return this.availablePropertyTypes.slice(0, this.limit);
    },
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    ...mapActions("property", [
      "getAllFacilities",
      "getPropertyTypes",
      "getFacilitiesCount",
    ]),
    currentConfigKey,
    getFacilityName: TranslationHelper.getFacilityName,
    getPropertyTypeName: TranslationHelper.getPropertyTypeName,
    showDistributor(type) {
      const count = this.getFacilityCount(type);
      if (count == "") return true;
      return Number(count) !== 0;
    },
    handleResize() {
      this.usePortal = window.innerWidth <= 767;
    },
    getFacilityCount(id) {
      if (id in this.facilitiesCountData) {
        return this.facilitiesCountData[id] || "0";
      }
      return Object.entries(this.facilitiesCountData).length ? "0" : "";
    },
    async loadFacilitiesCount(payload) {
      this.countPayload = payload;
      try {
        this.facilitiesCountData = await this.getFacilitiesCount({
          ...payload,
          ...this.getFilters(),
        });
      } catch (error) {
        this.facilitiesCountData = {};
      }
    },
    resetFacilitiesCount() {
      this.facilitiesCountData = {};
    },
    getFilters() {
      return {
        ratings: this.selectedRatings,
        propertyTypes: this.selectedTypes,
        facilities: this.selectedFacilities,
        distributorType: this.selectedDistributors,
      };
    },
    filtersApplied() {
      const {
        selectedRatings,
        selectedTypes,
        selectedFacilities,
        selectedDistributors,
      } = this;
      return [
        ...selectedTypes,
        ...selectedFacilities,
        ...selectedRatings,
        ...selectedDistributors,
      ].length;
    },
    showFilters() {
      this.show = true;
    },
    applyFilters: debounce(async function () {
      this.$emit("updated");
      this.loadFacilitiesCount(this.countPayload);
    }, 200),
    clearFilters() {
      if (this.hasFilters) {
        this.selectedTypes = [];
        this.selectedRatings = [];
        this.selectedFacilities = [];
        this.selectedDistributors = [];
        this.$emit("updated");
      }
    },
  },
};
</script>

<style>
@media only screen and (max-width: 767px) {
  .filter-wrapper {
    display: block !important;
    position: fixed;
    top: 0;
    background: #fff;
    width: 100%;
    min-height: calc(100vh - 60px);
    max-height: calc(100vh - 60px);
    z-index: 9999;
    overflow-y: auto;
    margin-top: 0 !important;
    padding: 20px;
  }

  .filter-wrapper .summary {
    border-top: 1px solid #ccc;
    background: #fff;
    padding: 10px;
    position: fixed;
    bottom: 0;
    left: 0;
    height: 60px;
    width: 100%;
    z-index: 99999;
  }
}
</style>
